import React, { useEffect, useState } from 'react';
import Get from '../../api/internal/Get';
import OptionList from '../../helpers/options/OptionList';
import Button from '../../assets/essentials/Button';
import classes from './CreateEvent.module.css';
import PutBody from '../../api/internal/PutBody';
import CloseModal from '../../helpers/modal/CloseModal';

type EditEventProps = {
  event: DriverEvent;
  driverOptions?: DriverOption[];
  eventTypeOptions?: EventTypeOption[];
  eventStatusOptions?: EventStatusOption[];
  onSuccess?: () => void;
};

const EditEvent = ({
  event,
  driverOptions: propDriverOptions,
  eventTypeOptions: propEventTypeOptions,
  eventStatusOptions: propEventStatusOptions,
  onSuccess,
}: EditEventProps) => {
  const [driverOptions, setDriverOptions] = useState<DriverOption[]>(
    propDriverOptions || [],
  );
  const [eventTypeOptions, setEventTypeOptions] = useState<EventTypeOption[]>(
    propEventTypeOptions || [],
  );
  const [eventStatusOptions, setEventStatusOptions] = useState<
    EventStatusOption[]
  >(propEventStatusOptions || []);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccessful, setIsSuccessful] = useState(false);

  const formatDateForInput = (dateValue?: string | Date | null) => {
    if (!dateValue) return '';
    const date =
      typeof dateValue === 'string' ? dateValue : dateValue.toISOString();
    return date.split('T')[0];
  };

  const formatTimeForInput = (dateValue?: string | Date | null) => {
    if (!dateValue) return '';
    const date =
      typeof dateValue === 'string' ? dateValue : dateValue.toISOString();
    const timePart = date.split('T')[1];
    return timePart ? timePart.substring(0, 5) : '';
  };

  console.log(event);

  const [formData, setFormData] = useState({
    driverId: event.driverId.toString(),
    eventTypeId: event.eventTypeId.toString(),
    eventStatusId: event.eventStatusId.toString(),
    requestedDate: formatDateForInput(event.requestedDate),
    startDate: formatDateForInput(event.startDate),
    startTime: formatTimeForInput(event.startDate),
    endDate: formatDateForInput(event.endDate),
    endTime: formatTimeForInput(event.endDate),
    isAllDay: event.isAllDay,
    notes: event.notes || '',
  });

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]:
        type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  useEffect(() => {
    if (!propDriverOptions) {
      Get('Employee/GetDriverOptions').then(res => {
        if (res) {
          setDriverOptions(res.data);
        }
      });
    }

    if (!propEventStatusOptions) {
      Get('Schedule/GetEventStatuses').then(res => {
        if (res) {
          setEventStatusOptions(res.data);
        }
      });
    }

    if (!propEventTypeOptions) {
      Get('Schedule/GetEventTypes').then(res => {
        if (res) {
          setEventTypeOptions(res.data);
        }
      });
    }
  }, [propDriverOptions, propEventStatusOptions, propEventTypeOptions]);

  const onUpdateSuccess = () => {
    setIsSuccessful(true);
    setIsLoading(false);
    onSuccess();
  };

  const handleDelete = () => {
    setIsLoading(true);

    // Combine date and time if not all day
    const startDateTime =
      formData.isAllDay || !formData.startTime
        ? formData.startDate
        : `${formData.startDate}T${formData.startTime}:00`;

    const endDateTime =
      formData.isAllDay || !formData.endTime || !formData.endDate
        ? formData.endDate || null
        : `${formData.endDate}T${formData.endTime}:00`;

    PutBody('Schedule/UpdateDriverEvent', {
      driverEventId: event.driverEventId,
      driverId: formData.driverId,
      eventTypeId: formData.eventTypeId,
      eventStatusId: formData.eventStatusId,
      requestedDate: formData.requestedDate,
      startDate: startDateTime,
      endDate: endDateTime,
      isAllDay: formData.isAllDay,
      notes: formData.notes,
      isActive: false,
    })
      .then(res => {
        if (res) {
          onUpdateSuccess();
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    setIsLoading(true);
    e.preventDefault();

    // Combine date and time if not all day
    const startDateTime =
      formData.isAllDay || !formData.startTime
        ? formData.startDate
        : `${formData.startDate}T${formData.startTime}:00`;

    const endDateTime =
      formData.isAllDay || !formData.endTime || !formData.endDate
        ? formData.endDate || null
        : `${formData.endDate}T${formData.endTime}:00`;

    PutBody('Schedule/UpdateDriverEvent', {
      driverEventId: event.driverEventId,
      driverId: formData.driverId,
      eventTypeId: formData.eventTypeId,
      eventStatusId: formData.eventStatusId,
      requestedDate: formData.requestedDate,
      startDate: startDateTime,
      endDate: endDateTime,
      isAllDay: formData.isAllDay,
      notes: formData.notes,
    })
      .then(res => {
        if (res) {
          onUpdateSuccess();
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <div>
      <div className={classes.headerContainer}>
        <h1 className={classes.header}>Edit Event</h1>
        <button
          className={classes.closeButton}
          onClick={CloseModal}
          type="button"
        >
          ×
        </button>
      </div>
      <form onSubmit={handleSubmit} className={classes.form}>
        <div className={classes.formGroup}>
          <label htmlFor="driverId">Driver</label>
          <select
            name="driverId"
            id="driverId"
            required
            value={formData.driverId}
            onChange={handleInputChange}
          >
            <option value="" hidden>
              Select a driver
            </option>
            <OptionList
              optionData={driverOptions}
              attributeID="driverId"
              attributeName="name"
            />
          </select>
        </div>

        <div className={classes.formGroup}>
          <label htmlFor="eventTypeId">Event Type</label>
          <select
            name="eventTypeId"
            id="eventTypeId"
            required
            value={formData.eventTypeId}
            onChange={handleInputChange}
          >
            <OptionList
              optionData={eventTypeOptions}
              attributeID="eventTypeId"
              attributeName="typeName"
            />
          </select>
        </div>

        <div className={classes.formGroup}>
          <label htmlFor="eventStatusId">Event Status</label>
          <select
            name="eventStatusId"
            id="eventStatusId"
            required
            value={formData.eventStatusId}
            onChange={handleInputChange}
          >
            <OptionList
              optionData={eventStatusOptions}
              attributeID="eventStatusId"
              attributeName="statusName"
            />
          </select>
        </div>

        <div className={classes.formRow}>
          <div className={classes.formGroup}>
            <label htmlFor="requestedDate">Requested Date</label>
            <input
              name="requestedDate"
              id="requestedDate"
              type="date"
              required
              value={formData.requestedDate}
              onChange={handleInputChange}
            />
          </div>
        </div>

        <div className={classes.formRow}>
          <div className={classes.formGroup}>
            <label htmlFor="startDate">Start Date</label>
            <input
              name="startDate"
              id="startDate"
              type="date"
              required
              value={formData.startDate}
              onChange={handleInputChange}
            />
          </div>

          {!formData.isAllDay && (
            <div className={classes.formGroup}>
              <label htmlFor="startTime">Start Time</label>
              <input
                name="startTime"
                id="startTime"
                type="time"
                value={formData.startTime}
                onChange={handleInputChange}
              />
            </div>
          )}

          <div className={classes.formGroup}>
            <label htmlFor="endDate">End Date</label>
            <input
              name="endDate"
              id="endDate"
              type="date"
              value={formData.endDate}
              onChange={handleInputChange}
            />
          </div>

          {!formData.isAllDay && (
            <div className={classes.formGroup}>
              <label htmlFor="endTime">End Time</label>
              <input
                name="endTime"
                id="endTime"
                type="time"
                value={formData.endTime}
                onChange={handleInputChange}
              />
            </div>
          )}
        </div>

        <div className={classes.checkboxGroup}>
          <label htmlFor="isAllDay">All Day Event</label>
          <input
            name="isAllDay"
            id="isAllDay"
            type="checkbox"
            checked={formData.isAllDay}
            onChange={handleInputChange}
          />
        </div>

        <div className={classes.formGroup}>
          <label htmlFor="notes">Notes</label>
          <textarea
            name="notes"
            id="notes"
            rows={4}
            value={formData.notes}
            onChange={handleInputChange}
          />
        </div>

        <div className={classes.submitButton}>
          <Button
            type="submit"
            variant="good"
            disabled={isLoading || isSuccessful}
            isLoading={isLoading}
          >
            Update Event
          </Button>
          <Button
            type="button"
            variant="bad"
            onClick={handleDelete}
            disabled={isLoading || isSuccessful}
            style={{ marginLeft: '1rem' }}
          >
            Delete Event
          </Button>
          {isSuccessful && <p>Updated ✅</p>}
        </div>
      </form>
    </div>
  );
};

export default EditEvent;
